import io from 'socket.io-client';
import authService from './auth.service';

export interface SocketUser {
  userId: string;
  mapId?: string;
}

export interface MapUpdateData {
  mapId: string;
  data: any;
  timestamp: number;
  updateType: 'full' | 'partial';
}

export interface DataResponse {
  data: any[];
  timestamp: number;
}

export interface UpdateCellRequest {
  i: number;
  c: number[];
  t?: string;
  l?: string;
}

export interface CheckoutRequest {
  cells: Array<{ i?: number; x: number; y: number }>;
}

export interface CheckoutResponse {
  success: boolean;
  billId?: string;
  expAt?: number; // expiration timestamp in milliseconds
  qr?: string; // QR code image URL
  errorCells?: Array<{ i?: number; x: number; y: number; reason: string }>;
}

export interface CheckoutCellInfo {
  cell: number;
  expAt: number;
}

export interface CheckoutListResponse {
  cells: CheckoutCellInfo[];
}

export interface CheckoutUpdateData {
  cells: CheckoutCellInfo[];
}

export interface PaymentSuccessData {
  billId: string;
  message: string;
}

export interface SocketEvents {
  // Client to Server
  'get-data': (data: { mapId: string }) => void;
  'sync-data': (data: { mapId: string; timestamp: number }) => void;
  'update-cell': (data: UpdateCellRequest) => void;
  'checkout': (data: CheckoutRequest) => void;

  // Server to Client
  'join-success': (data: {
    message: string;
    username: string;
    isAuthenticated: boolean;
  }) => void;
  'data-response': (data: DataResponse) => void;
  'cell-update': (data: DataResponse) => void;
  'map-update': (data: MapUpdateData) => void;
  'checkout-response': (data: CheckoutResponse) => void;
  'checkout-list': (data: CheckoutListResponse) => void;
  'checkout-update': (data: CheckoutUpdateData) => void;
  'payment-success': (data: PaymentSuccessData) => void;
  error: (data: { message: string; code?: string }) => void;
}

class SocketService {
  private socket: any | null = null;
  private baseURL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3042';

  connect(): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        const token = authService.getAccessToken();

        this.socket = io(this.baseURL, {
          auth: {
            token: token || undefined,
          },
          transports: ['websocket', 'polling'],
        });

        this.socket.on('connect', () => {
          console.log('Socket connected:', this.socket?.id);
          resolve(this.socket!);
        });

        this.socket.on('connected', (data: any) => {
          console.log('Socket authenticated:', data);
        });

        this.socket.on('error', (data: any) => {
          console.error('Socket error:', data);
          reject(new Error(data.message));
        });

        this.socket.on('disconnect', (reason: any) => {
          console.log('Socket disconnected:', reason);
        });

        this.socket.on('connect_error', (error: any) => {
          console.error('Socket connection error:', error);
          reject(error);
        });
      } catch (error) {
        console.error('Failed to create socket connection:', error);
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      console.log('Socket disconnected');
    }
  }

  /**
   * Reconnect socket with new token (e.g., after login)
   * This will disconnect the current socket and create a new connection with updated token
   */
  async reconnect(): Promise<any> {
    console.log('Reconnecting socket with new token...');

    // Disconnect current socket if exists
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Connect again with new token
    return this.connect();
  }

  getSocket(): any | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Map related methods
  getData(mapId: string): void {
    if (this.socket && this.socket.connected) {
      console.log('[GET-DATA] Requesting full map data:', mapId);
      this.socket.emit('get-data', { mapId });
    } else {
      console.warn('Socket not connected, cannot get data');
    }
  }

  syncData(mapId: string, timestamp: number): void {
    if (this.socket && this.socket.connected) {
      console.log('[SYNC-DATA] Requesting sync with timestamp:', timestamp);
      this.socket.emit('sync-data', { mapId, timestamp });
    } else {
      console.warn('Socket not connected, cannot sync data');
    }
  }

  updateCell(data: UpdateCellRequest): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('update-cell', data);
    } else {
      console.warn('Socket not connected, cannot update cell');
    }
  }

  checkout(data: CheckoutRequest): void {
    if (this.socket && this.socket.connected) {
      console.log('[CHECKOUT] Sending checkout request:', data);
      this.socket.emit('checkout', data);
    } else {
      console.warn('Socket not connected, cannot checkout');
    }
  }

  // Event listeners
  onJoinSuccess(
    callback: (data: {
      message: string;
      username: string;
      isAuthenticated: boolean;
    }) => void
  ): void {
    if (this.socket) {
      this.socket.on('join-success', callback);
    }
  }

  onDataResponse(callback: (data: DataResponse) => void): void {
    if (this.socket) {
      this.socket.on('data-response', callback);
    }
  }

  onCellUpdate(callback: (data: DataResponse) => void): void {
    if (this.socket) {
      this.socket.on('cell-update', callback);
    }
  }

  onMapUpdate(callback: (data: MapUpdateData) => void): void {
    if (this.socket) {
      this.socket.on('map-update', callback);
    }
  }

  onError(callback: (data: { message: string; code?: string }) => void): void {
    if (this.socket) {
      this.socket.on('error', callback);
    }
  }

  onCheckoutResponse(callback: (data: CheckoutResponse) => void): void {
    if (this.socket) {
      console.log('[SocketService] Setting up checkout-response listener');
      this.socket.on('checkout-response', (data: any) => {
        console.log('[SocketService] Received checkout-response event:', data);
        callback(data);
      });
    } else {
      console.warn('[SocketService] Socket not available, cannot setup checkout-response listener');
    }
  }

  offCheckoutResponse(callback?: (data: CheckoutResponse) => void): void {
    if (this.socket) {
      console.log('[SocketService] Removing checkout-response listener');
      this.socket.off('checkout-response', callback);
    }
  }

  onCheckoutList(callback: (data: CheckoutListResponse) => void): void {
    if (this.socket) {
      console.log('[SocketService] Setting up checkout-list listener');
      this.socket.on('checkout-list', (data: any) => {
        console.log('[SocketService] Received checkout-list event:', data);
        callback(data);
      });
    }
  }

  offCheckoutList(callback?: (data: CheckoutListResponse) => void): void {
    if (this.socket) {
      this.socket.off('checkout-list', callback);
    }
  }

  onCheckoutUpdate(callback: (data: CheckoutUpdateData) => void): void {
    if (this.socket) {
      console.log('[SocketService] Setting up checkout-update listener');
      this.socket.on('checkout-update', (data: any) => {
        console.log('[SocketService] Received checkout-update event:', data);
        callback(data);
      });
    }
  }

  offCheckoutUpdate(callback?: (data: CheckoutUpdateData) => void): void {
    if (this.socket) {
      this.socket.off('checkout-update', callback);
    }
  }

  onPaymentSuccess(callback: (data: PaymentSuccessData) => void): void {
    if (this.socket) {
      console.log('[SocketService] Setting up payment-success listener');
      this.socket.on('payment-success', (data: any) => {
        console.log('[SocketService] Received payment-success event:', data);
        console.log('[SocketService] Event data type:', typeof data, 'Is object:', typeof data === 'object');
        try {
          callback(data);
        } catch (error) {
          console.error('[SocketService] Error in payment-success callback:', error);
        }
      });
    } else {
      console.warn('[SocketService] Socket not available, cannot setup payment-success listener');
    }
  }

  offPaymentSuccess(callback?: (data: PaymentSuccessData) => void): void {
    if (this.socket) {
      this.socket.off('payment-success', callback);
    }
  }

  // Remove event listeners
  offJoinSuccess(
    callback?: (data: {
      message: string;
      username: string;
      isAuthenticated: boolean;
    }) => void
  ): void {
    if (this.socket) {
      this.socket.off('join-success', callback);
    }
  }

  offDataResponse(callback?: (data: DataResponse) => void): void {
    if (this.socket) {
      this.socket.off('data-response', callback);
    }
  }

  offCellUpdate(callback?: (data: DataResponse) => void): void {
    if (this.socket) {
      this.socket.off('cell-update', callback);
    }
  }

  offMapUpdate(callback?: (data: MapUpdateData) => void): void {
    if (this.socket) {
      this.socket.off('map-update', callback);
    }
  }

  offError(
    callback?: (data: { message: string; code?: string }) => void
  ): void {
    if (this.socket) {
      this.socket.off('error', callback);
    }
  }
}

export const socketService = new SocketService();
export default socketService;
